import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DataTable, LazyLoadEvent } from 'primeng/primeng';
import { Subscription } from 'rxjs/Subscription';
import { DepositSearchResult } from '../../../models/depositSearchResult.model';
import { FilterCriteria } from '../../../models/filter-criteria';
import { RemittanceEntry } from '../../../models/remittance-entry.model';
import { AccountingPipe } from '../../../pipes/accounting.Pipe';
import { AuthorizationService } from '../../../services/authorization.service';
import { GlobalFilterService } from '../../../services/global-filter.service';
import { RemittanceEntryService } from '../../../services/remittance-entry/remittance-entry.service';
import { RemittanceStageService } from '../../../services/remittance-entry/remittance-stage.service';
import { ModalConfirmComponent } from '../../modal-confirm/modal-confirm.component';
import { RemittanceStageTableHelper } from './remittance-stage.tableHelper';

@Component({
  selector: 'app-remittance-stage',
  templateUrl: './remittance-stage.component.html',
  styleUrls: ['./remittance-stage.component.css'],
})

//  This is a Smart component
export class RemittanceStageComponent implements OnInit, OnDestroy {
  private readonly MAX_STAGED_REMITTANCES = 10000
  @ViewChild('remittances') remittanceChild: DataTable
  @ViewChild('modalConfirmChild') modalConfirmComponent: ModalConfirmComponent
  @Input('checkReferenceNumber') checkReferenceNumber: number;

  //  Subscriptions
  onCheckDetail: Subscription;
  onCreateRemittance: Subscription
  onUpdateRemittance: Subscription
  onDeleteRemittance: Subscription

  //  Form controls
  public remittanceStageForm: FormGroup;
  readonly tableHelper: RemittanceStageTableHelper;

  //  Members
  clientId: string
  private _checkDetail: DepositSearchResult;
  private remittanceToDelete: RemittanceEntry
  get checkDetail(): DepositSearchResult {
    return this._checkDetail;
  }
  modalConfirm: boolean;
  modalHeader: string;
  deleteMessage: string;
  formProcessing: boolean;
  totalItemCount: number;
  onlyInvalid: boolean;

  private filterCriteria = new FilterCriteria('Batch_SequenceNumber', -1, 15, 0)

  get rowLimit(): number {
    return this.filterCriteria.limit
  }

  public remittanceEntries: RemittanceEntry[];

  //  TODO: Add events for:
  //  1.  Editing a field/row
  //  2.  Deleting a row
  //  3.  Change notification for Remittance Summary to update totals.

  constructor(
    private authorizationService: AuthorizationService,
    private globalFilterService: GlobalFilterService,
    private remittanceStageService: RemittanceStageService,
    private remittanceEntryService: RemittanceEntryService,
    private formBuilder: FormBuilder,
  ) {
    this.tableHelper = new RemittanceStageTableHelper();
    //  Subscribe to the Check Detail notification. This normally occurs
    //  when the user navigates to the Remittance Entry page from the Deposit
    //  Search page.
    this.onCheckDetail = this.remittanceEntryService.checkDetailSource$.subscribe(
      (check) => {
        this._checkDetail = check;
        this.loadData()
      },
      (error) => {
        console.log(`Error occurred at ${new Date()} status: ${error.status.toString()}`)
      },
      () => { },   //  Complete
    );
    if (this.globalFilterService.selectedEnvironment) {
      this.clientId = this.globalFilterService.selectedEnvironment.ClientId4Digit
    }
}

  ngOnInit() {
    this.createSubscriptions()
    this.initForm();
    this.totalItemCount = 0;
    // this.loadData()
  }

  ngOnDestroy(): void {
    this.onCheckDetail.unsubscribe()
    this.onCreateRemittance.unsubscribe()
    this.onUpdateRemittance.unsubscribe()
    this.onDeleteRemittance.unsubscribe()
  }

  private createSubscriptions(): void {
    //  Subscribe to Create Remittance notification. This requires the grid to refresh.
    //  Filter criteria are reset to defaults when a remittance is created/updated.
    this.onCreateRemittance = this.remittanceEntryService.createRemittanceSource$.subscribe(() => {
      this.filterCriteria = new FilterCriteria('Batch_SequenceNumber', -1, 15, 0)
      this.loadData()
      this.resetGrid()
    })

    this.onUpdateRemittance = this.remittanceEntryService.updateRemittanceSource$.subscribe(() => {
      this.filterCriteria = new FilterCriteria('Batch_SequenceNumber', -1, 15, 0)
      this.loadData()
      this.resetGrid()
    })

    this.onDeleteRemittance = this.remittanceEntryService.remittanceStageDeleteSource$.subscribe(() => {
      this.filterCriteria = new FilterCriteria('Batch_SequenceNumber', -1, 15, 0)
      this.loadData()
      this.resetGrid()
    })
  }

  private async loadData(): Promise<void> {
    this.callNotifyService();
    this.formProcessing = true;

    await this.remittanceStageService.loadStagedRemittances(this.clientId,
      this.checkDetail.Check_Ref_Number,
      this.filterCriteria, this.onlyInvalid)
      .then((data) => {
        this.remittanceEntries = []
        this.remittanceEntries = data.Items
        this.totalItemCount = data.TotalItemCount
        this.formProcessing = false
      })
      .catch((error: any) => {
        toastr.error('An error occurred retrieving Staged Remittances. If this problem continues please contact your account team.');
        this.formProcessing = false
      })
  }

  // Filter down the remittance stage table to only invalid entries
  showOnlyInvalid() {
    this.onlyInvalid = !this.onlyInvalid;
    this.loadData()
  }

  toAdjustmentCodeDisplay(entry: RemittanceEntry): string {
    let display = ''
    let numberOfCodes = 0

    if (entry.Adjustment_Code_1) {
      display += (entry.Adjustment_Code_1 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_2) {
      display += (entry.Adjustment_Code_2 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_3) {
      display += (entry.Adjustment_Code_3 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_4) {
      display += (entry.Adjustment_Code_4 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_5) {
      display += (entry.Adjustment_Code_5 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_6 && numberOfCodes < 5) {
      display += (entry.Adjustment_Code_6 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_7 && numberOfCodes < 5) {
      display += (entry.Adjustment_Code_7 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_8 && numberOfCodes < 5) {
      display += (entry.Adjustment_Code_8 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_9 && numberOfCodes < 5) {
      display += (entry.Adjustment_Code_9 + ',')
      numberOfCodes += 1
    }
    if (entry.Adjustment_Code_1 && numberOfCodes < 5) {
      display += (entry.Adjustment_Code_10 + ',')
      numberOfCodes += 1
    }
    //  Remove final trailing comma.
    display = display.replace(/,*$/, '');
    return display
  }

  private initForm() {
    this.remittanceStageForm = this.formBuilder.group({
      remittances: new FormControl(''),
    });
  }

  // Add custom class rules to table rows
  rowStyle(rowData: RemittanceEntry): string {
    // Rule for invalid entries
    if (rowData.Valid_Posted_Claim === "N") {
      return "invalid-remitt-entry text-danger bg-warning";
    }
    return "";
  }

  //  Display the complete set of staged remittances in the order they were entered.
  showAllRecords(): void {
    this.filterCriteria.limit = this.MAX_STAGED_REMITTANCES
    this.filterCriteria.sort = 'Batch_SequenceNumber'
    this.filterCriteria.sortOrder = 1
    this.filterCriteria.offset = 0
    this.loadData()
  }

  editRemittance(remittance: RemittanceEntry) {
    this.remittanceStageService.onEditRemittance(remittance)
  }

  deleteRemittance(remittance: RemittanceEntry) {
    this.modalHeader = 'Confirm Delete';
    this.deleteMessage = `Are you sure you want to delete the following Remittance Stage record?\n\n Store ID: ${
      remittance.StoreId
      }\n Rx Number: ${
      remittance.Prescription_PrescriptionNumber
      }\n Fill Date: ${
      new DatePipe('en-US').transform(remittance.Prescription_FillDate, 'MM/dd/yyyy')
      }\n Amount: ${
      new AccountingPipe().transform(new CurrencyPipe('en-US').transform(remittance.Money_PaidAmount, 'USD', true, '1.2-2'))
      }`;
    this.modalConfirm = true;
    this.remittanceToDelete = remittance
  }

  onModalConfirmChange(event: boolean) {
    this.modalConfirm = event;
  }

  async onDeleteConfirmAction(event: any) {
    if (event.type === 'OK') {
      this.modalConfirm = false; // Gracefully release the modal window
      this.formProcessing = true;
      this.callNotifyService();
      await this.remittanceStageService.deleteRemittance(this.globalFilterService.selectedEnvironment.ClientId4Digit, this.remittanceToDelete)
        .then(() => {
          toastr.info('Remittance Stage record successfully deleted.');
          this.formProcessing = false;
        })
        .catch(() => {
          toastr.error('An error occurred deleting a Remittance Stage record. If this problem continues please contact your account team.');
          this.formProcessing = false;
        });
    }
  }

  async sort(event) {
    this.filterCriteria.sort = event.field
    this.filterCriteria.sortOrder = event.order
    this.filterCriteria.offset = 0
    await this.loadData()
  }

  async paginate(event) {
    this.filterCriteria.offset = event.first;
    await this.loadData()
  }

  private resetGrid(): void {
    this.remittanceChild.first = 0
  }

  private callNotifyService() {
    if (!this.authorizationService.HealthcareTopBar || !this.authorizationService.HealthcareTopBar.notifyActivity) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/remittance-entry/remittance-stage/remittance-stage.component.ts